import { LinkClickImpression } from "@jmc/core/src/components/LinkClickImpression/index";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { WarningMessage } from "@jmc/solid-design-system/src/components/atoms/WarningMessage/WarningMessage";
import classnames from "classnames";
import InnerHTML from "dangerously-set-html-content";
import React, { ReactElement, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { useCookie } from "react-use";
import usePortal from "react-useportal";

import { useScript } from "../../../../design_system/src/hooks/useScript";
import style from "./style.module.scss";

const liveValidationRegex =
    /<script type="text\/javascript"\s*src="(https:\/\/img\d\d\.en25\.com\/i\/livevalidation_standalone\.compressed\.js)">[\s\n]*<\/script>/;

interface Props {
    id: string;
    form: string;
    className?: string;
    placement?: string;
}

const parseForm = (form: string): string => {
    let tempForm = form;
    const regex = /class[ \t]*=[ \t]*"[^"]+"/gi;
    let result;
    const classes = [];
    while ((result = regex.exec(form))) {
        for (const [key, value] of Object.entries(style)) {
            if (result[0].includes(key)) {
                classes.push({ original: result[0], new: result[0].replace(key, `${key} ${value}`) });
            }
        }
    }

    classes.forEach((c) => {
        tempForm = tempForm.replace(c.original, c.new);
    });

    // the liveValidation script needs to be loaded before the embedded script tag in the tempForm is evaluated.
    // so we load it via useScript and only render the html from the CMS only after it is loaded.
    // But then we don't want to embed that same script tag in the DOM again, otherwise the liveValidation script
    // compares different function references and it doesn't validate mandatory fields.
    tempForm = tempForm.replace(liveValidationRegex, "");

    return tempForm;
};

export const EloquaForm = ({ id, form, className, placement }: Props): ReactElement => {
    const { t } = useTranslation();
    const recaptchaRef = React.useRef<ReCAPTCHA>(null);
    const [value] = useCookie("ELOQUA");

    const topRef = useRef(null);

    const { Portal } = usePortal({
        bindTo: typeof window !== "undefined" && document && document.querySelector(`#${id} form`),
    });

    const liveValidationScriptSrc =
        form.match(liveValidationRegex)?.[1] || "https://img06.en25.com/i/livevalidation_standalone.compressed.js";
    const scriptStatus = useScript(liveValidationScriptSrc);

    //adding GUID to Eloqua form
    useEffect(() => {
        if (scriptStatus !== "ready") {
            return;
        }
        const formDom = topRef.current.querySelector(`#${id} form`) as Element;
        let element = '<input type="hidden" name="elqCustomerGUID" />';
        if (value && value.split("=")[0] === "GUID") {
            element = `<input value="${value.split("=")[1]}" type="hidden" name="elqCustomerGUID" />`;
        }
        formDom.insertAdjacentHTML("afterbegin", element);
    }, [value, scriptStatus]);

    return (
        <div ref={topRef} id={id}>
            <>
                {scriptStatus === "ready" && <InnerHTML html={parseForm(form)} className={classnames(className)} />}
                {scriptStatus === "blocked" && (
                    <div className={style.blocked}>
                        <div className={style.warning}>
                            <WarningMessage
                                warning={t(
                                    "common:It seems your browser is blocking functional cookies which are required to display forms. To resolve this issue, please update your settings to allow these cookies.",
                                )}
                                variant="card"
                            >
                                <LinkClickImpression
                                    name="EloquaForm Cookies warning"
                                    targetUrl={null}
                                    placement={placement}
                                    external={false}
                                >
                                    <button
                                        className={style.cookieLink}
                                        onClick={(e) => (window?.OneTrust?.ToggleInfoDisplay(), e.preventDefault())}
                                    >
                                        <Typography size="s" variant="link">
                                            {t("Open cookie settings", { ns: "common" })}
                                        </Typography>
                                    </button>
                                </LinkClickImpression>
                            </WarningMessage>
                        </div>
                    </div>
                )}
                {process.env.GATSBY_RECAPTCHA_SITE_KEY !== "false" && (
                    <Portal>
                        <ReCAPTCHA
                            sitekey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
                            ref={recaptchaRef}
                            size="invisible"
                        />
                    </Portal>
                )}
            </>
        </div>
    );
};

export default EloquaForm;
